import {
  BG_STYLES,
  BORDER_STYLES,
  CaretDownIcon,
  COLORS,
  COMMON_STYLES,
  LazyImage,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from '@dehaat/dds'
import React from 'react'
import {
  View,
  StyleSheet,
  Text,
  StyleProp,
  ViewStyle,
  Pressable,
} from 'react-native'

import ProductIcon from '../../icons/ProductIcon'
import { FarmerProducts, IdNameImage } from '../../models/farmer'

interface Props {
  crop: IdNameImage
  products: FarmerProducts['products']
  handleCTAClick?: () => void
  containerStyles?: StyleProp<ViewStyle>
}

const ImageCountView = ({ count }: { count: number }) => {
  return (
    <View
      style={[
        styles.productContainer,
        COMMON_STYLES.b1,
        BORDER_STYLES.borderNeutral00,
        COMMON_STYLES['br1/2'],
        BG_STYLES.bgNeutral10,
        COMMON_STYLES.alignItemsCenter,
        COMMON_STYLES.justifyCenter,
      ]}
    >
      <Text
        style={[
          TYPOGRAPHY.HEADING3,
          VARIANTS_STYLES.MEDIUM,
          TEXT_STYLES.textNeutral90,
        ]}
      >
        +{count}
      </Text>
    </View>
  )
}

const FarmerProductCard = ({
  crop,
  products,
  containerStyles,
  handleCTAClick,
}: Props) => {
  return (
    <View
      style={[
        COMMON_STYLES.b1,
        BORDER_STYLES.borderBlue20,
        BG_STYLES.bgWhite,
        COMMON_STYLES.br1,
        COMMON_STYLES.ofHidden,
        styles.container,
        containerStyles,
      ]}
    >
      <View
        style={[
          COMMON_STYLES.ph1,
          COMMON_STYLES['pv3/4'],
          COMMON_STYLES.row,
          COMMON_STYLES.alignItemsCenter,
        ]}
      >
        <LazyImage
          imageUrl={crop.image_url}
          imgStyles={styles.cropImage}
          fallBackIcon={<View style={styles.cropImage}>{crop.name?.[0]}</View>}
        />
        <View style={COMMON_STYLES['ml0.5']}>
          <Text
            style={[
              TYPOGRAPHY.CAPTION2,
              VARIANTS_STYLES.SEMI_EMPHASIZED,
              TEXT_STYLES.textNeutral80,
            ]}
            numberOfLines={1}
          >
            Products used in
          </Text>
          <Text
            style={[
              TYPOGRAPHY.TEXT1,
              VARIANTS_STYLES.MEDIUM,
              TEXT_STYLES.textNeutral90,
            ]}
          >
            {crop.name}
          </Text>
        </View>
      </View>

      <View
        style={[
          COMMON_STYLES.ph1,
          COMMON_STYLES['pv3/4'],
          COMMON_STYLES.row,
          COMMON_STYLES.justifyStart,
          styles['gap1/4'],
        ]}
      >
        {products
          .slice(0, products.length > 3 ? 2 : products.length)
          .map((product, index) => (
            <View
              style={[
                styles.productContainer,
                COMMON_STYLES.b1,
                BORDER_STYLES.borderNeutral10,
                COMMON_STYLES['br1/2'],
              ]}
              key={index}
            >
              <View
                style={[
                  COMMON_STYLES.flex1,
                  COMMON_STYLES.alignItemsCenter,
                  COMMON_STYLES.justifyCenter,
                  COMMON_STYLES['ph1/2'],
                ]}
              >
                <LazyImage
                  imageUrl={product.image_url}
                  imgStyles={styles.img80X80}
                  fallBackIcon={<ProductIcon height={40} width={40} />}
                />
              </View>

              <View
                style={[
                  BG_STYLES.bgNeutral10,
                  COMMON_STYLES.fullWidth,
                  COMMON_STYLES.alignItemsCenter,
                  COMMON_STYLES.justifyCenter,
                  COMMON_STYLES['ph1/2'],
                  styles.h18,
                ]}
              >
                <Text
                  style={[TYPOGRAPHY.CAPTION2, VARIANTS_STYLES.SEMI_EMPHASIZED]}
                  numberOfLines={1}
                >
                  {product.product_name}
                </Text>
              </View>
            </View>
          ))}
        {products.length > 3 && <ImageCountView count={products.length - 2} />}
      </View>
      <Pressable
        onPress={handleCTAClick}
        style={({ pressed }) => [
          pressed && COMMON_STYLES['opacity1/2'],
          COMMON_STYLES.row,
          COMMON_STYLES.justifyCenter,
          COMMON_STYLES.alignItemsCenter,
          styles.h48,
        ]}
      >
        <Text
          style={[
            TYPOGRAPHY.TEXT2,
            VARIANTS_STYLES.SEMI_EMPHASIZED,
            TEXT_STYLES.textPrimary100,
          ]}
        >
          View All Products
        </Text>
        <CaretDownIcon
          svgProps={{
            ...styles.caretIcon,
            style: styles.rotateIcon,
          }}
          pathProps={{ stroke: COLORS.primary100 }}
        />
      </Pressable>
    </View>
  )
}

const styles = StyleSheet.create({
  cropImage: {
    width: 36,
    height: 36,
    borderWidth: 1,
    borderColor: COLORS.neutral00,
    borderRadius: 18,
  },
  container: {
    width: 328,
  },
  productContainer: {
    height: 98,
    width: 96,
  },
  img80X80: {
    height: 80,
    width: 80,
  },
  'gap1/4': {
    gap: 4,
  },
  h18: {
    height: 18,
  },
  h48: {
    height: 48,
  },
  caretIcon: { height: 20, width: 20 },
  rotateIcon: {
    transform: [{ rotate: '-90deg' }],
  },
})

export default FarmerProductCard
